import React, { useMemo } from 'react'
import type { Location } from 'react-router-dom'

import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'

type Props = {
  location: Location
  onDismiss: () => void
}

/**
 * SamlForm component for SAML SSO authentication
 *
 * Provides a form that initiates SAML SSO login by posting to the SAML authentication endpoint.
 * Includes redirect URI handling and an optional fallback to email/password login.
 *
 * @param props - Component props
 * @param props.location - Current location information containing query parameters for redirect
 * @param props.onDismiss - Callback function to dismiss SAML form and return to email/password login
 * @returns JSX element containing the SAML login form
 */
const SamlForm = ({ location, onDismiss }: Props): React.ReactNode => {
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )
  return (
    <form
      method="POST"
      action={
        '/api-auth/saml-login/?redirect_uri=' +
        encodeURI(
          '/login?next=' +
            encodeURIComponent(
              searchParams.get('return') || searchParams.get('next') || '/',
            ),
        )
      }
    >
      <ButtonContainer vertical>
        <Button
          appearance="primary"
          type="submit"
          large
          caption="Continue with SAML SSO"
          fullWidth
          textCenter
        />
        {window.location.search.includes('?emaillogin') && (
          <Button
            type="submit"
            caption="Continue with email/password"
            large
            onClick={onDismiss}
            fullWidth
            textCenter
          />
        )}
      </ButtonContainer>
    </form>
  )
}

export default SamlForm
